/**
 * Domain enums — master spec §5.
 *
 * **Every enum-typed column has a defined domain and a check constraint**, and
 * the check constraint lives in the migration. These arrays are the SAME
 * domains on the TypeScript side, and a Phase 3 test asserts they still match
 * what the database will accept — a drift between the two otherwise shows up
 * as a bare `23514` carrying no useful message.
 */

import { z } from 'zod';

/** §5.1: three values, a check constraint, not a table. */
export const REGIONS = ['AMER', 'EMEA', 'APAC'] as const;
export type Region = (typeof REGIONS)[number];
export const regionSchema = z.enum(REGIONS);

export const SEGMENTS = [
  'defense_aerospace',
  'test_measurement',
  'industrial',
  'medical',
  'semicap',
  'ev_energy',
  'other',
] as const;
export type Segment = (typeof SEGMENTS)[number];
export const segmentSchema = z.enum(SEGMENTS);

export const CHANNEL_TYPES = ['direct', 'rep', 'distributor'] as const;
export type ChannelType = (typeof CHANNEL_TYPES)[number];
export const channelTypeSchema = z.enum(CHANNEL_TYPES);

export const FIRM_TYPES = ['rep', 'distributor'] as const;
export type FirmType = (typeof FIRM_TYPES)[number];
export const firmTypeSchema = z.enum(FIRM_TYPES);

export const PRODUCT_FAMILIES = [
  'rf_switch',
  'high_voltage_driver',
  'general_purpose_switch',
  'evaluation_board',
  // Session 7, decision 2: MM562x/MM5630 are not RF switches.
  'loopback_switch',
  'other',
] as const;
export type ProductFamily = (typeof PRODUCT_FAMILIES)[number];
export const productFamilySchema = z.enum(PRODUCT_FAMILIES);

/**
 * Business units are **reference data**, not an enum (docs/bugs.md session 7,
 * decision 2): the `business_units` table is the one list, and every filter,
 * report and form reads it — through `/api/reference` on the web and
 * `loadBusinessUnits()` in the API. There is deliberately no array of codes
 * here, so nothing can quietly keep a second copy.
 *
 * A code is validated for shape only; whether it exists, and whether it may
 * still be chosen, is the table's to say.
 */
export const BUSINESS_UNIT_STATUSES = ['active', 'legacy', 'retired'] as const;
export type BusinessUnitStatus = (typeof BUSINESS_UNIT_STATUSES)[number];
export const businessUnitStatusSchema = z.enum(BUSINESS_UNIT_STATUSES);

export const businessUnitCodeSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,39}$/, 'A business unit code is lower-case letters, digits and _');

export const LIFECYCLE_STATUSES = ['concept', 'sampling', 'production', 'nrnd', 'eol'] as const;
export type LifecycleStatus = (typeof LIFECYCLE_STATUSES)[number];
export const lifecycleStatusSchema = z.enum(LIFECYCLE_STATUSES);

export const TECHNOLOGY_TYPES = [
  'em_relay',
  'reed_relay',
  'pin_diode',
  'solid_state_relay',
  'mems',
  'other',
] as const;
export type TechnologyType = (typeof TECHNOLOGY_TYPES)[number];
export const technologyTypeSchema = z.enum(TECHNOLOGY_TYPES);

export const ACCOUNT_STATUSES = ['prospect', 'active', 'dormant'] as const;
export type AccountStatus = (typeof ACCOUNT_STATUSES)[number];
export const accountStatusSchema = z.enum(ACCOUNT_STATUSES);

export const BUYING_ROLES = [
  'design_engineer',
  'program_manager',
  'procurement',
  'quality',
  'executive',
  'channel_sales',
  'other',
] as const;
export type BuyingRole = (typeof BUYING_ROLES)[number];
export const buyingRoleSchema = z.enum(BUYING_ROLES);

export const INFLUENCE_LEVELS = ['low', 'medium', 'high', 'decision_maker'] as const;
export type InfluenceLevel = (typeof INFLUENCE_LEVELS)[number];
export const influenceLevelSchema = z.enum(INFLUENCE_LEVELS);

export const DISPOSITIONS = ['champion', 'neutral', 'detractor', 'unknown'] as const;
export type Disposition = (typeof DISPOSITIONS)[number];
export const dispositionSchema = z.enum(DISPOSITIONS);

export const END_MARKETS = [
  'radar',
  'ew',
  'satcom',
  'avionics',
  'ate',
  'semicap',
  'medical_imaging',
  'ev_powertrain',
  'industrial_automation',
  'comms',
  'other',
] as const;
export type EndMarket = (typeof END_MARKETS)[number];
export const endMarketSchema = z.enum(END_MARKETS);

export const PROGRAM_STATUSES = ['concept', 'development', 'production', 'cancelled'] as const;
export type ProgramStatus = (typeof PROGRAM_STATUSES)[number];
export const programStatusSchema = z.enum(PROGRAM_STATUSES);

/**
 * §5.3. `won` is reached by advancing to `DESIGN_WIN` (§6.2); the other
 * terminal values are reached by closing. `on_hold` is not terminal.
 *
 * `invalid` — "Invalid — not a real opportunity" — is how a deal that should
 * never have existed is set aside (docs/bugs.md session 3, decision 12).
 * Opportunities are never deleted. Only a Manager or an Admin moves a deal
 * into or out of it (`canChangeOpportunityStatus` in `authz.ts`), and it is
 * left out of every list, total and report unless it is asked for by name.
 */
export const OPPORTUNITY_STATUSES = [
  'open',
  'won',
  'lost',
  'no_decision',
  'on_hold',
  'design_win_lapsed',
  'invalid',
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];
export const opportunityStatusSchema = z.enum(OPPORTUNITY_STATUSES);

/** The status that sets a deal aside instead of deleting it (decision 12). */
export const INVALID_STATUS = 'invalid' as const;

/**
 * Every status except `invalid`: what a list shows when nobody named a status.
 * "All statuses" in the opportunity list means these; Invalid appears only
 * when it is chosen.
 */
export const VISIBLE_STATUSES = OPPORTUNITY_STATUSES.filter(
  (status): status is Exclude<OpportunityStatus, 'invalid'> => status !== INVALID_STATUS,
);

/**
 * `status=all` — "All statuses". The opportunity list writes it to the URL,
 * and every API filter that takes `status` accepts it.
 *
 * It means `VISIBLE_STATUSES`: every status **except Invalid**, the same set
 * as naming no status at all. Session 3 (decision 12) settled that an Invalid
 * deal appears only when it is named, so `status=all,invalid` is how to ask
 * for literally everything.
 */
export const ALL_STATUSES_TOKEN = 'all' as const;

/**
 * A `status` include list with `all` expanded to `VISIBLE_STATUSES`. Every
 * other value passes through unchanged, so an unknown code still matches
 * nothing rather than being dropped.
 */
export function expandStatusFilter(values: readonly string[]): string[] {
  if (!values.includes(ALL_STATUSES_TOKEN)) return [...values];
  const expanded = new Set<string>();
  for (const value of values) {
    if (value === ALL_STATUSES_TOKEN) VISIBLE_STATUSES.forEach((s) => expanded.add(s));
    else expanded.add(value);
  }
  return [...expanded];
}

/**
 * The `outcome` values — §5.1 [R4]: these, never loose labels like `win`/`loss`.
 * `invalid` is an outcome so that closing as Invalid appends a closure row like
 * any other close; it is in no win/loss figure.
 */
export const OUTCOMES = ['won', 'lost', 'no_decision', 'design_win_lapsed', 'invalid'] as const;
export type Outcome = (typeof OUTCOMES)[number];
export const outcomeSchema = z.enum(OUTCOMES);

/** The statuses that require a matching latest closure (§5.5). Identical to the outcomes. */
export const TERMINAL_STATUSES = OUTCOMES;

export const FORECAST_CATEGORIES = [
  'pipeline',
  'upside',
  'best_case',
  'commit',
  'omitted',
] as const;
export type ForecastCategory = (typeof FORECAST_CATEGORIES)[number];
export const forecastCategorySchema = z.enum(FORECAST_CATEGORIES);

export const LEAD_SOURCES = [
  'existing_relationship',
  'origin_unknown',
  'representative_referral',
  'supplier_referral',
  'distributor_nbo',
  'trade_show',
  'web_inquiry',
  'outbound',
  'conference_paper',
  'other',
] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];
export const leadSourceSchema = z.enum(LEAD_SOURCES);

export const PRICE_BASES = ['menlo_net', 'distributor_resale'] as const;
export type PriceBasis = (typeof PRICE_BASES)[number];
export const priceBasisSchema = z.enum(PRICE_BASES);

export const LINE_TYPES = ['primary', 'alternate', 'design_support'] as const;
export type LineType = (typeof LINE_TYPES)[number];
export const lineTypeSchema = z.enum(LINE_TYPES);

export const REALIZATION_STATUSES = [
  'not_applicable',
  'pending_qual',
  'qualified',
  'first_order',
  'production',
] as const;
export type RealizationStatus = (typeof REALIZATION_STATUSES)[number];
export const realizationStatusSchema = z.enum(REALIZATION_STATUSES);

export const MEETING_TYPES = [
  'call',
  'video',
  'onsite_visit',
  'design_review',
  'trade_show',
  'internal_review',
  'email_thread',
] as const;
export type MeetingType = (typeof MEETING_TYPES)[number];
export const meetingTypeSchema = z.enum(MEETING_TYPES);

export const SENTIMENTS = ['positive', 'neutral', 'negative', 'unknown'] as const;
export type Sentiment = (typeof SENTIMENTS)[number];
export const sentimentSchema = z.enum(SENTIMENTS);

export const ATTENDEE_TYPES = ['internal', 'customer', 'partner'] as const;
export type AttendeeType = (typeof ATTENDEE_TYPES)[number];
export const attendeeTypeSchema = z.enum(ATTENDEE_TYPES);

export const ACTION_OWNER_TYPES = ['internal', 'customer'] as const;
export type ActionOwnerType = (typeof ACTION_OWNER_TYPES)[number];
export const actionOwnerTypeSchema = z.enum(ACTION_OWNER_TYPES);

export const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type Priority = (typeof PRIORITIES)[number];
export const prioritySchema = z.enum(PRIORITIES);

export const ACTION_STATUSES = ['open', 'in_progress', 'blocked', 'done', 'cancelled'] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];
export const actionStatusSchema = z.enum(ACTION_STATUSES);

/** The statuses that keep an action item live: in the digest, and overdue-able. */
export const OPEN_ACTION_STATUSES = ['open', 'in_progress', 'blocked'] as const;

export const NOTE_ENTITY_TYPES = ['opportunity', 'account', 'contact', 'program'] as const;
export type NoteEntityType = (typeof NOTE_ENTITY_TYPES)[number];
export const noteEntityTypeSchema = z.enum(NOTE_ENTITY_TYPES);

export const NOTE_TYPES = [
  'call_note',
  'technical',
  'competitive_intel',
  'pricing',
  'risk',
  'general',
] as const;
export type NoteType = (typeof NOTE_TYPES)[number];
export const noteTypeSchema = z.enum(NOTE_TYPES);

export const ATTACHMENT_ENTITY_TYPES = [
  'opportunity',
  'account',
  'program',
  'contact',
  'meeting',
] as const;
export type AttachmentEntityType = (typeof ATTACHMENT_ENTITY_TYPES)[number];
/**
 * The entity types the GENERIC upload routes accept. `account_strategy` is
 * deliberately absent: a strategy image is marked at creation, through the
 * strategy's own presign route, so it can never exist unmarked (see
 * `docs/account-strategy.md`).
 */
export const attachmentEntityTypeSchema = z.enum(ATTACHMENT_ENTITY_TYPES);

/**
 * The attachment entity type a strategy image carries. Its `entity_id` is the
 * ACCOUNT id, and it resolves to the account for writes; every read path applies
 * the strategy read rule to it, whatever its upload or link state.
 */
export const STRATEGY_ATTACHMENT_ENTITY_TYPE = 'account_strategy' as const;

/** Every value `attachments.entity_type` may hold, for rows read back. */
export const STORED_ATTACHMENT_ENTITY_TYPES = [
  ...ATTACHMENT_ENTITY_TYPES,
  STRATEGY_ATTACHMENT_ENTITY_TYPE,
] as const;
export type StoredAttachmentEntityType = (typeof STORED_ATTACHMENT_ENTITY_TYPES)[number];
export const storedAttachmentEntityTypeSchema = z.enum(STORED_ATTACHMENT_ENTITY_TYPES);

export const UPLOAD_STATES = ['pending', 'confirmed'] as const;
export type UploadState = (typeof UPLOAD_STATES)[number];
export const uploadStateSchema = z.enum(UPLOAD_STATES);

/** §8a.1 content provenance — the one expensive retrofit, present since Phase 2. */
export const CONTENT_SOURCES = [
  'human',
  'ai_suggested',
  'ai_extracted',
  'imported',
  'system',
] as const;
export type ContentSource = (typeof CONTENT_SOURCES)[number];
export const contentSourceSchema = z.enum(CONTENT_SOURCES);

export const EXPORT_EVENT_TYPES = [
  'attestation_no',
  'attestation_blocked',
  'policy_acknowledged',
  'pattern_warning',
  'import_blocked',
  'attachment_attested',
] as const;
export type ExportEventType = (typeof EXPORT_EVENT_TYPES)[number];
export const exportEventTypeSchema = z.enum(EXPORT_EVENT_TYPES);

export const EMAIL_TYPES = ['action_digest', 'assignment'] as const;
export type EmailType = (typeof EMAIL_TYPES)[number];
export const emailTypeSchema = z.enum(EMAIL_TYPES);

export const AUDIT_ACTIONS = ['create', 'update', 'delete', 'export'] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];
export const auditActionSchema = z.enum(AUDIT_ACTIONS);

/**
 * Stage codes, listed in `sort_order`.
 *
 * §5.1 [R3]: **nothing compares stage codes as strings.** Every ordering
 * question goes through `stages.sort_order`, which the database supplies
 * through the join `v_opportunity_effective` already makes. This array exists
 * to type request bodies, not to compare with.
 */
export const STAGE_CODES = [
  'PROSPECT',
  'QUALIFIED',
  'DESIGN_EVAL',
  'DESIGN_IN',
  'DESIGN_WIN',
  'QUALIFICATION',
  'FIRST_ORDER',
  'PRODUCTION',
] as const;
export type StageCode = (typeof STAGE_CODES)[number];
export const stageCodeSchema = z.enum(STAGE_CODES);

/**
 * `stages.sort_order` of `DESIGN_WIN`. §6.2: advancing to it **or to any stage
 * above it** closes the commercial opportunity as won. Compared on
 * `sort_order`, never on the code (§5.1 [R3]).
 *
 * It lives here rather than in `apps/api/src/import/plan.ts`, where it was
 * first declared, because the account strategy's "future part" rule needs it
 * too — and a service importing the importer would pull the workbook parser in
 * with it. Both import it from here.
 */
export const DESIGN_WIN_SORT_ORDER = 50;
